import React, { useRef, useCallback, useLayoutEffect } from 'react';

interface HoldButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  onClick: () => void;
  holdDelay?: number;
  holdInterval?: number;
}

/**
 * A button that fires onClick once on press, then fires repeatedly while held.
 * Uses the Pointer Events API so mouse and touch are handled identically with
 * no ghost/duplicate events. setPointerCapture ensures pointerup is received
 * even if the pointer leaves the element during a hold.
 *
 * onClick is stored in a ref so the interval always calls the latest version —
 * this fixes the stale-closure bug where React re-renders pass a new onClick
 * on each state update but the already-running interval kept the old one.
 */
const HoldButton: React.FC<HoldButtonProps> = ({
  onClick,
  holdDelay = 600,
  holdInterval = 80,
  disabled,
  children,
  ...rest
}) => {
  const timerRef    = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const repeatRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  // Always keep a current reference to onClick so the interval never goes stale
  const onClickRef  = useRef(onClick);
  useLayoutEffect(() => { onClickRef.current = onClick; });

  const stop = useCallback(() => {
    if (timerRef.current)  { clearTimeout(timerRef.current);   timerRef.current  = null; }
    if (repeatRef.current) { clearInterval(repeatRef.current); repeatRef.current = null; }
  }, []);

  const start = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled) return;
    // Only respond to the primary pointer (left mouse button, first touch)
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Capture so pointerup fires on this element even if pointer leaves it
    e.currentTarget.setPointerCapture(e.pointerId);
    stop();
    onClickRef.current();
    timerRef.current = setTimeout(() => {
      repeatRef.current = setInterval(() => onClickRef.current(), holdInterval);
    }, holdDelay);
  }, [holdDelay, holdInterval, disabled, stop]);

  return (
    <button
      {...rest}
      disabled={disabled}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerCancel={stop}
      // Prevent the browser's synthetic click event from firing onClick a second time
      onClick={(e) => e.preventDefault()}
    >
      {children}
    </button>
  );
};

export default HoldButton;
